import { T } from "./Translate";
import SelectLanguage from "./SelectLanguage";
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Toolbar,
  IconButton,
  AppBar,
  Avatar,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Skeleton,
  Snackbar,
  Alert,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Settings as SettingsIcon } from "../icons";
import { Build as BuildIcon } from "../icons";
import { Policy as PolicyIcon } from "../icons";
import CircularProgress from "@mui/material/CircularProgress";
import { ExitToApp as ExitToAppIcon } from "../icons";
import { AutoStories as AutoStoriesIcon } from "../icons";
// ...existing code...
import { Payment as PaymentIcon } from "../icons";
import { Storefront as StorefrontIcon } from "../icons";
import { Edit as EditIcon } from "../icons";
import BackButton from "./BackButton";
import { auth, uploadImage, updateUserProfile } from "../firebase";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { endSession, isLoggedIn } from "../session";
import { useNavigate } from "react-router-dom";
import BottomToolbar from "./BottomToolbar";
import { Person as PersonIcon } from "../icons";
import { QrCode2 as QrCode2Icon } from "../icons";
import { Help as HelpIcon } from "../icons";
import { Share as ShareIcon } from "../icons";
import { Bookmark as BookmarkIcon } from "../icons";
import { QueryStats as QueryStatsIcon } from "../icons";
import { ReceiptLong as ReceiptLongIcon } from "../icons";
import { ShoppingBag as ShoppingBagIcon } from "../icons";
import { barColors, brandColors } from "../theme";
import { isIOSApp } from "../utils/device";

const menuItems = (uid) => {
  // Orden manual por uso
  return [
    { text: "Editar Perfil", icon: <SettingsIcon />, path: "/edit" },
    { text: "Mi perfíl", icon: <PersonIcon />, path: `/profile/${uid}` },
    { text: "Guardados", icon: <BookmarkIcon />, path: "/saved" },
    { text: "Mis compras", icon: <ShoppingBagIcon />, path: "/my-purchases" },
    { text: "Mis Tiendas", icon: <StorefrontIcon />, path: "/my-stores" },
    { text: "Publicaciones", icon: <EditIcon />, path: "/editOffers" },
    { text: "Estadísticas", icon: <QueryStatsIcon />, path: "/statistics" },
    { text: "Generar QR de cobro", icon: <QrCode2Icon />, path: "/PaymentQRGenerator" },
    { text: "Link a PayPal", icon: <ShareIcon />, path: "https://www.paypal.com", external: true },
    ...(isIOSApp() ? [] : [{ text: "Suscripciones", icon: <PaymentIcon />, path: "/subscription" }]),
    { text: "Ajustes", icon: <BuildIcon />, path: "/settings" },
    { text: "Idioma", icon: <SelectLanguage />, path: "/language" },
    { text: "Soporte", icon: <HelpIcon />, path: "/support" },
    { text: "Política de Privacidad", icon: <PolicyIcon />, path: "/privacyPolicy" },
    { text: "Terminos y condiciones", icon: <AutoStoriesIcon />, path: "/terms" },
    { text: "Política de Reembolsos", icon: <ReceiptLongIcon />, path: "/refunds" },
  ];
};

const AccountMenu = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageUploading, setImageUploading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (userAuth) => {
      if (userAuth) {
        try {
          const db = getFirestore();
          const userDocRef = doc(db, "users", userAuth.uid);
          const userDocSnapshot = await getDoc(userDocRef);

          if (userDocSnapshot.exists()) {
            setUser({ uid: userAuth.uid, ...userDocSnapshot.data() });
          }
        } catch (err) {
          setError("Error al cargar los datos del usuario.");
          console.error("Error fetching user data:", err);
        } finally {
          setLoading(false);
        }
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImageUploading(true);
    try {
      const imageUrl = await uploadImage(file);
      if (user) {
        await updateUserProfile(user.uid, { profileImageUrl: imageUrl });
        setUser((prevUser) => ({ ...prevUser, profileImageUrl: imageUrl }));
      }
    } catch (err) {
      setError("Error al subir la imagen. Inténtalo de nuevo.");
      console.error("Error uploading image:", err);
    } finally {
      setImageUploading(false);
    }
  };

  const onLogout = () => {
    endSession();
    navigate("/login");
  };

  const handleCloseSnackbar = () => {
    setError(null);
  };

  return (
    <Box>
      <AppBar position="fixed" sx={{ backgroundColor: barColors.background, color: barColors.text }}>
        <Toolbar sx={{ display: "flex", justifyContent: "center", p: "10px", width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", maxWidth: { xs: "100%", md: "980px" }, mx: "auto" }}>
            <BackButton color="inherit" />
            {loading ? (
              <Skeleton variant="circular" width={56} height={56} sx={{ ml: 2 }} />
            ) : (
              <Box sx={{ position: "relative", ml: 2 }}>
                <IconButton component="label" htmlFor="upload-button">
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      border: `2.5px solid ${barColors.avatarBorder}`,
                    }}
                    alt="Perfil"
                    src={user?.profileImageUrl}
                  />
                  {imageUploading && (
                    <CircularProgress
                      size={56}
                      sx={{
                        color: barColors.text,
                        position: "absolute",
                        top: 0,
                        left: 0,
                        zIndex: 1,
                      }}
                    />
                  )}
                </IconButton>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: "none" }}
                  id="upload-button"
                  disabled={imageUploading}
                />
              </Box>
            )}
            <Box sx={{ ml: 2 }}>
              {loading ? (
                <>
                  <Skeleton variant="text" width={150} />
                  <Skeleton variant="text" width={100} />
                </>
              ) : (
                <>
                  <Typography variant="h5" sx={{ color: barColors.text }}>
                    {user?.displayName || <T>Usuario</T>}
                  </Typography>
                  <Typography variant="body2" sx={{ color: barColors.text }}>
                    {user?.email}
                  </Typography>
                </>
              )}
            </Box>
            <Box sx={{ flexGrow: 1 }} />
          </Box>
        </Toolbar>
      </AppBar>
      <Container
        sx={{
          mt: "90px",
          mb: 4,
          maxWidth: { xs: "100%", md: "980px" },
          minHeight: "calc(100vh - 216px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <List>
          {loading ? (
            <>
              {menuItems().map((item, index) => (
                <React.Fragment key={index}>
                  <ListItem disablePadding>
                    <ListItemButton>
                      <ListItemIcon>
                        <Skeleton variant="circular" width={24} height={24} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={<Skeleton variant="text" width="60%" />} 
                      />
                    </ListItemButton>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </>
          ) : (
            menuItems(user?.uid).map((item, index, arr) => (
              <React.Fragment key={item.text}>
                {index > 0 && <Divider />}
                <ListItem disablePadding>
                  <ListItemButton
                    component={item.external ? 'a' : (!item.isIdioma ? RouterLink : undefined)}
                    href={item.external ? item.path : undefined}
                    to={item.external ? undefined : (!item.isIdioma ? item.path : undefined)}
                    target={item.external ? '_blank' : undefined}
                    rel={item.external ? 'noopener noreferrer' : undefined}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={<T>{item.text}</T>} />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))
          )}
        </List>
        <Box sx={{ flexGrow: 1 }} />
        {/* Sección de tiendas ahora es un botón en la lista principal */}
        <Box sx={{ p: 2 }}>
          <Button
            variant="contained"
            color="error"
            startIcon={<ExitToAppIcon />}
            onClick={onLogout}
            fullWidth
          >
            <T>Cerrar sesión</T>
          </Button>
        </Box>
        {!loading && (
          <Typography
            variant="caption"
            align="center"
            sx={{ display: "block", color: brandColors.textSecondary, mt: 2, mb: 10 }}
          >
            © 2026 Dapur Tecnología - Notiline
          </Typography>
        )}
      </Container>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
      <BottomToolbar />
    </Box>
  );
};

export default AccountMenu;